import React, { Component, createRef } from 'react';
import { connect } from 'react-redux';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import PropTypes from 'prop-types';
import { getChartTypeMenuItems } from '../../../../Components/Menu/MenuItemsHelper';
import { CSV, HIGHCHARTS_MENU, PNG, XLS } from '../../../../Utils/Constants';
import applyExporting from 'highcharts/modules/exporting';
import applyOffline from 'highcharts/modules/offline-exporting';
import applyExportData from 'highcharts/modules/export-data';
import Menu from '../../../../Components/Menu/Menu';
import DownloadIcon from '../../../../Components/Buttons/DownloadIcon';
import './AttendeesInSessionChart.css';
import moment from 'moment-timezone';
import {
  DOWNLOAD_CHART,
  MixpanelChartType,
  PNG as mixpanel_PNG,
  CSV as mixpanel_CSV,
  EXCEL as mixpanel_EXCEL,
  VALUE_ADD,
  SESSION_DETAILS
} from '../../../../Telemetry/Mixpanel/Constants/mixpanelConstants';
import {
  trackAnalyticsChartType,
  trackAnalyticsFeature
} from '../../../../Telemetry/Mixpanel/Features/analyticsTracker';
import { attendeesInSessionDesktopSkeletonScreen } from '../../../../SkeletonScreens/SkeletonScreen';

export class AttendeesInSessionChart extends Component {
  static contextTypes = {
    t: PropTypes.func.isRequired
  };

  static propTypes = {
    reduced_session_details: PropTypes.object,
    user: PropTypes.object
  };
  constructor(props) {
    super(props);
    this.chartRef = createRef();
  }
  componentDidMount() {
    applyExporting(Highcharts);
    applyOffline(Highcharts);
    applyExportData(Highcharts);
  }

  onClickHandler = id => {
    let type = MixpanelChartType.ATTENDEES_IN_SESSION_CHART;
    trackAnalyticsFeature(VALUE_ADD, SESSION_DETAILS, DOWNLOAD_CHART);
    if (id === PNG) {
      this.chartRef.current.chart.exportChart();
      trackAnalyticsChartType(type, mixpanel_PNG);
    } else if (id === XLS) {
      this.chartRef.current.chart.downloadXLS();
      trackAnalyticsChartType(type, mixpanel_EXCEL);
    } else if (id === CSV) {
      this.chartRef.current.chart.downloadCSV();
      trackAnalyticsChartType(type, mixpanel_CSV);
    }
  };

  render() {
    const { attendeesInSessionInfoOverTime, fetchingAttendeesInSessionsOverTime } = this.props.reduced_session_details;
    const { user } = this.props;
    if (fetchingAttendeesInSessionsOverTime) {
      return <div className="attendeesInSessionChartLayout">{attendeesInSessionDesktopSkeletonScreen()}</div>;
    }
    const { t } = this.context;
    const detailsMenu = [];
    const menuItems = getChartTypeMenuItems(HIGHCHARTS_MENU);
    menuItems.map(item =>
      detailsMenu.push({
        onClick: () => this.onClickHandler(item.id),
        text: item.title,
        iconClassName: item.iconClass,
        id: item.id
      })
    );

    return (
      <div className="attendeesInSessionChartLayout">
        <div className="panelHeader">
          <div className="panelTitle">{t('sessionDetails.numberOfAttendees')}</div>
          <div>
            <Menu
              menuKey="attendeesInSessionChartDownload"
              buttonName={t('buttons.downloadCamelCase')}
              detailsMenu={detailsMenu}
              tooltipText={t('chart.downloadTooltipText')}
              buttonComponent={<DownloadIcon />}
            />
          </div>
        </div>
        <div className="panelBody">
          <HighchartsReact
            ref={this.chartRef}
            highcharts={Highcharts}
            constructorType={'chart'}
            options={this.getChartOptions(attendeesInSessionInfoOverTime, user)}
          />
        </div>
      </div>
    );
  }

  getChartOptions = (attendeesInSessionInfoOverTime, user) => {
    const { t } = this.context;
    const darkMode = user && user.darkMode;
    return {
      colors: ['#8a5be4'],
      chart: {
        type: 'line',
        height: 220,
        zoomType: 'x',
        backgroundColor: darkMode ? '#444444' : '#ffffff'
      },
      legend: {
        enabled: false
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      exporting: {
        enabled: false,
        csv: {
          decimalPoint: null
        },
        type: 'image/png',
        url: 'https://export.highcharts.com/'
      },
      xAxis: {
        tickWidth: 0,
        categories: this.getXAxisTimeArray(attendeesInSessionInfoOverTime),
        crosshair: {
          width: '1',
          color: '#b4b6c3',
          dashStyle: 'solid',
          labels: {
            style: {
              color: darkMode ? '#ffffff' : '#666666'
            }
          }
        }
      },
      yAxis: [
        {
          min: 0,
          title: '',
          tickAmount: 5,
          allowDecimals: false,
          labels: {
            style: {
              color: darkMode ? '#ffffff' : '#666666'
            }
          }
        }
      ],

      tooltip: {
        useHTML: true,
        shared: true,
        formatter: AttendeesInSessionChart.formatTooltip,
        borderRadius: '2',
        borderWidth: '0',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.12), 0 5px 15px 0 rgba(0, 0, 0, 0.08)',
        backgroundColor: darkMode ? '#2A2A2A' : '#ffffff',
        style: {
          color: darkMode ? '#ffffff' : '#444444',
          fontSize: '0.75em',
          fontFamily: 'Lato, sans-serif'
        }
      },
      plotOptions: {
        line: {
          marker: {
            enabled: false
          }
        }
      },
      series: [
        {
          name: t('tableTopStat.attendees'),
          type: 'line',
          data: this.getSessionAttendeeCountInfoArray(attendeesInSessionInfoOverTime)
        }
      ]
    };
  };

  getSessionAttendeeCountInfoArray = attendeesInSessionInfoOverTime => {
    const { t } = this.context;

    let sessionAttendeeCountInfo = attendeesInSessionInfoOverTime.attendeeCountsOverTime.map(item => {
      return {
        dateTime: moment(item.dateTime)
          .tz(attendeesInSessionInfoOverTime.timeZone)
          .format(t('formatting.timeWithZone')),
        y: item.count
      };
    });
    return sessionAttendeeCountInfo;
  };

  getXAxisTimeArray = attendeesInSessionInfoOverTime => {
    const { t } = this.context;

    let getXAxisTimeArray = attendeesInSessionInfoOverTime.attendeeCountsOverTime.map(item => {
      return moment(item.dateTime)
        .tz(attendeesInSessionInfoOverTime.timeZone)
        .format(t('formatting.timeWithHoursAndMinutes'));
    });
    return getXAxisTimeArray;
  };

  static formatTooltip(tooltip, x = this.x, points = this.points) {
    let formattedStartTime = points[0].point.dateTime;
    let data = '';
    points.forEach(point => {
      data += `<div style="display: flex; flex-direction: row; justify-content: space-between; padding-bottom: 16px">
      <div style="font-size: 16px; font-weight: 500; font-style: normal; font-stretch: normal; line-height: normal;
      letter-spacing: normal;">${point.series.name}</div>
      <div style="font-size: 18px; font-weight: bold; font-style: normal; font-stretch: normal; line-height: normal;
      letter-spacing: normal;">${point.point.y}</div>
    </div>`;
    });
    return `<br/>
    <div style="font-size: 14px; font-weight: 500; font-style: normal; font-stretch: normal; line-height: normal;
    letter-spacing: normal;">
      ${formattedStartTime}
    </div>
    <hr style="border-top: 1px solid #d0d2dd; margin-top: 11px; margin-bottom: 16px;" />
    ${data}
    `;
  }
}

export default connect(state => ({
  reduced_session_details: state.reduced_session_details,
  user: state.reduced_user
}))(AttendeesInSessionChart);



// WEBPACK FOOTER //
// ./src/GoToWebinar/Sessions/SessionDetails/Chart/AttendeesInSessionChart.js